import React, { useMemo, useState, useCallback } from 'react';
import { Column, Task } from '../services/taskboardService';
import '../styles/TaskCalendarView.css';

/**
 * Calendar view for a task board.
 *
 * Tasks render as horizontal bars spanning startDate → dueDate, laid out with
 * a lane-packing algorithm so overlapping bars never collide (the same
 * approach Gantt/timeline views use).
 *
 * Scalability is the main design constraint: a day can hold many tasks, so
 * each week row shows a bounded number of lanes and collapses the rest into a
 * "+N more" affordance rather than growing without limit.
 *
 * Only tasks with at least one date appear. A task with just a due date is
 * treated as a single-day item on that date.
 */

export interface CalendarTask extends Task {
  columnId: string;
  columnTitle: string;
  columnColor: string;
}

interface Props {
  columns: Column[];
  /** Already filtered by the parent's search/filter controls. */
  tasks: CalendarTask[];
  onTaskClick: (columnId: string, taskId: string) => void;
}

/** Lanes rendered per week before the rest collapse into "+N more". */
const MAX_LANES = 4;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ── Date helpers (local-time, no external dependency) ──────────────────

/** Midnight local. Parses 'YYYY-MM-DD' as local, not UTC — `new Date()` on a
 *  bare date string is treated as UTC and can shift a day across timezones. */
function parseDate(value?: string): Date | null {
  if (!value) return null;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function daysBetween(a: Date, b: Date): number {
  // Round rather than floor: DST transitions make some "days" 23 or 25 hours.
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

/** Monday-first week start, matching the WEEKDAYS header. */
function startOfWeek(d: Date): Date {
  const out = startOfDay(d);
  const dow = (out.getDay() + 6) % 7; // Mon=0 … Sun=6
  return addDays(out, -dow);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
const DAY_FMT   = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

// ── Layout ─────────────────────────────────────────────────────────────

interface Segment {
  task: CalendarTask;
  /** Column index within the week, 0–6. */
  start: number;
  /** Inclusive end index within the week, 0–6. */
  end: number;
  /** True when the task actually begins before this week. */
  clippedStart: boolean;
  /** True when the task actually ends after this week. */
  clippedEnd: boolean;
  lane: number;
}

/**
 * Packs segments into lanes so no two in the same lane overlap.
 * Greedy first-fit over segments pre-sorted by start, then by longest span —
 * long bars settle into low lanes, which keeps the layout stable as tasks are
 * added and avoids a ragged appearance.
 */
function packLanes(segments: Omit<Segment, 'lane'>[]): Segment[] {
  const sorted = [...segments].sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    const spanA = a.end - a.start;
    const spanB = b.end - b.start;
    if (spanA !== spanB) return spanB - spanA;
    return a.task.title.localeCompare(b.task.title);
  });

  const laneEnds: number[] = []; // last occupied column index per lane
  return sorted.map(seg => {
    let lane = laneEnds.findIndex(end => end < seg.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(seg.end);
    } else {
      laneEnds[lane] = seg.end;
    }
    return { ...seg, lane };
  });
}

// ── Component ──────────────────────────────────────────────────────────

const TaskCalendarView: React.FC<Props> = ({ tasks, onTaskClick }) => {
  const [cursor, setCursor] = useState<Date>(() => startOfDay(new Date()));
  // Weeks the user has expanded past MAX_LANES, keyed by week start time.
  const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set());

  const today = useMemo(() => startOfDay(new Date()), []);

  /** Resolve each task to a concrete [start, end] range. */
  const dated = useMemo(() => {
    const out: { task: CalendarTask; start: Date; end: Date }[] = [];
    for (const task of tasks) {
      const s = parseDate(task.startDate);
      const e = parseDate(task.dueDate);
      if (!s && !e) continue; // undated tasks never appear

      // A task with only one date is a single-day item.
      let start = s ?? e!;
      let end = e ?? s!;
      // Tolerate inverted ranges rather than dropping the task.
      if (end < start) [start, end] = [end, start];

      out.push({ task, start, end });
    }
    return out;
  }, [tasks]);

  /** The visible grid: whole weeks covering the current month. */
  const weeks = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(cursor));
    const monthEnd = endOfMonth(cursor);
    const gridEnd = addDays(startOfWeek(monthEnd), 6);
    const totalDays = daysBetween(gridStart, gridEnd) + 1;

    const result: { weekStart: Date; days: Date[] }[] = [];
    for (let i = 0; i < totalDays; i += 7) {
      const weekStart = addDays(gridStart, i);
      result.push({
        weekStart,
        days: Array.from({ length: 7 }, (_, d) => addDays(weekStart, d)),
      });
    }
    return result;
  }, [cursor]);

  /** Split each task range into per-week segments and pack them into lanes. */
  const segmentsByWeek = useMemo(() => {
    return weeks.map(({ weekStart }) => {
      const weekEnd = addDays(weekStart, 6);
      const raw: Omit<Segment, 'lane'>[] = [];

      for (const { task, start, end } of dated) {
        if (end < weekStart || start > weekEnd) continue; // no overlap

        const clampedStart = start < weekStart ? weekStart : start;
        const clampedEnd = end > weekEnd ? weekEnd : end;

        raw.push({
          task,
          start: daysBetween(weekStart, clampedStart),
          end: daysBetween(weekStart, clampedEnd),
          clippedStart: start < weekStart,
          clippedEnd: end > weekEnd,
        });
      }

      return packLanes(raw);
    });
  }, [weeks, dated]);

  const goToday = useCallback(() => setCursor(startOfDay(new Date())), []);
  const goPrev = useCallback(
    () => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1)), []);
  const goNext = useCallback(
    () => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1)), []);

  const toggleWeek = useCallback((weekStartTime: number) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(weekStartTime)) next.delete(weekStartTime);
      else next.add(weekStartTime);
      return next;
    });
  }, []);

  const visibleCount = dated.length;

  return (
    <div className="tcal">
      <div className="tcal-toolbar">
        <div className="tcal-nav">
          <button className="tcal-nav-btn" onClick={goPrev} aria-label="Previous month">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
          </button>
          <button className="tcal-nav-btn" onClick={goNext} aria-label="Next month">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
          </button>
          <button className="tcal-today-btn" onClick={goToday}>Today</button>
        </div>

        <h3 className="tcal-title">{MONTH_FMT.format(cursor)}</h3>

        <div className="tcal-legend">
          <span className="tcal-count">
            {visibleCount} scheduled {visibleCount === 1 ? 'task' : 'tasks'}
          </span>
        </div>
      </div>

      <div className="tcal-grid" role="grid">
        <div className="tcal-weekdays" role="row">
          {WEEKDAYS.map(d => (
            <div key={d} className="tcal-weekday" role="columnheader">{d}</div>
          ))}
        </div>

        {weeks.map(({ weekStart, days }, wi) => {
          const segments = segmentsByWeek[wi];
          const weekKey = weekStart.getTime();
          const isExpanded = expandedWeeks.has(weekKey);
          const laneCount = segments.reduce((m, s) => Math.max(m, s.lane + 1), 0);
          const shownLanes = isExpanded ? laneCount : Math.min(laneCount, MAX_LANES);
          const hiddenByDay = new Array(7).fill(0);

          for (const seg of segments) {
            if (seg.lane >= shownLanes) {
              for (let d = seg.start; d <= seg.end; d++) hiddenByDay[d]++;
            }
          }
          // Show the toggle when something is hidden, OR when this week is
          // expanded — otherwise expanding removes the only control that
          // could collapse it again, stranding the user in the tall layout.
          const hasHidden = hiddenByDay.some(n => n > 0);
          const showToggle = hasHidden || (isExpanded && laneCount > MAX_LANES);

          return (
            <div className="tcal-week" key={weekKey} role="row">
              {/* Day cells — the backdrop the bars sit on. */}
              <div className="tcal-daycells">
                {days.map(day => {
                  const isToday = sameDay(day, today);
                  const outside = day.getMonth() !== cursor.getMonth();
                  return (
                    <div
                      key={day.getTime()}
                      className={
                        'tcal-daycell'
                        + (outside ? ' is-outside' : '')
                        + (isToday ? ' is-today' : '')
                      }
                      role="gridcell"
                    >
                      <span className="tcal-daynum">
                        {isToday ? <span className="tcal-today-pill">{day.getDate()}</span> : day.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Task bars, positioned over the day grid. */}
              <div
                className="tcal-lanes"
                style={{ minHeight: shownLanes ? shownLanes * 30 + 8 : 12 }}
              >
                {segments
                  .filter(seg => seg.lane < shownLanes)
                  .map(seg => {
                    const span = seg.end - seg.start + 1;
                    const overdue =
                      !!seg.task.dueDate
                      && parseDate(seg.task.dueDate)! < today
                      && !/done|complete|closed/i.test(seg.task.columnTitle);

                    return (
                      <button
                        key={`${seg.task.id}-${seg.lane}-${seg.start}`}
                        className={
                          'tcal-bar'
                          + (seg.clippedStart ? ' clip-start' : '')
                          + (seg.clippedEnd ? ' clip-end' : '')
                          + (overdue ? ' is-overdue' : '')
                        }
                        style={{
                          left: `calc(${(seg.start / 7) * 100}% + 4px)`,
                          width: `calc(${(span / 7) * 100}% - 8px)`,
                          top: seg.lane * 30,
                          // Column colour drives the bar, so the calendar reads
                          // as the same board the user already knows.
                          ['--bar-color' as string]: seg.task.columnColor || 'var(--brand-500)',
                        }}
                        onClick={() => onTaskClick(seg.task.columnId, seg.task.id)}
                        title={
                          `${seg.task.title}\n${seg.task.columnTitle}`
                          + `\n${DAY_FMT.format(parseDate(seg.task.startDate) ?? parseDate(seg.task.dueDate)!)}`
                          + ` – ${DAY_FMT.format(parseDate(seg.task.dueDate) ?? parseDate(seg.task.startDate)!)}`
                        }
                      >
                        {seg.task.assignee && (
                          <span className="tcal-bar-avatar" aria-hidden="true">
                            {seg.task.assignee.trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="tcal-bar-title">{seg.task.title}</span>
                        {overdue && <span className="tcal-bar-flag" title="Overdue">!</span>}
                      </button>
                    );
                  })}
              </div>

              {showToggle && (
                <button
                  className="tcal-more"
                  onClick={() => toggleWeek(weekKey)}
                >
                  {isExpanded
                    ? 'Show less'
                    : `+${Math.max(...hiddenByDay)} more`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {visibleCount === 0 && (
        <div className="tcal-empty">
          <div className="tcal-empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                 strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
          </div>
          <p className="tcal-empty-title">No scheduled tasks</p>
          <p className="tcal-empty-body">
            Tasks appear here once they have a start or due date.
          </p>
        </div>
      )}
    </div>
  );
};

export default TaskCalendarView;
